// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useEffect, useMemo } from "react";
import { setStorageScope } from "./lib/scopedStorage";
import { AuthContext, useAuthProvider, useAuth } from "./hooks/useAuth";
import { StorageContext } from "./lib/persistence";
import { localStorageProvider } from "./lib/localStorage";
import { SupabaseStorageProvider } from "./lib/supabaseStorage";
import { supabase } from "./lib/supabase";
import OELogo from "./components/OELogo";
import AuthPage from "./components/AuthPage";
import LandingPage from "./components/LandingPage";
import ProjectPicker from "./components/ProjectPicker";
import OpenEstimate from "./OpenEstimate";
import { buildSandboxProject, SANDBOX_ORG_PARTNERS } from "./projectData";
import ConceptualBudgetEditor from "./components/ConceptualBudgetEditor";
import InsightsView from "./components/InsightsView";
import SecurityPage from "./components/SecurityPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import TermsPage from "./components/TermsPage";
import CookieBanner from "./components/CookieBanner";
import OnboardingModal, { shouldShowOnboarding, markOnboardingSeen } from "./components/OnboardingModal";
import { identifyUser, resetIdentity, track, capturePageview, Events } from "./lib/analytics";
import * as Icon from "./components/OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

// ── Hash-based routing helpers ──
function parseHash(): { route: string; id?: string } {
  const h = window.location.hash.replace(/^#\/?/, "");
  if (!h) return { route: "" };
  const [route, ...rest] = h.split("/");
  return { route, id: rest.join("/") || undefined };
}

function setHash(path: string) {
  const target = path ? `#/${path}` : "#/";
  if (window.location.hash !== target) window.history.pushState(null, "", target);
}

function AppContent() {
  const { user, loading, signOut, emailVerified, isPasswordRecovery, clearPasswordRecovery } = useAuth();
  // Storage is per-user from here down. Set synchronously in render so every
  // child's useState initializer already sees the right prefix.
  setStorageScope(user?.id);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentProjectName, setCurrentProjectName] = useState<string | null>(null);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [settingsTab, setSettingsTab] = useState<string | null>(null);
  const [sandboxMode, setSandboxMode] = useState(false);
  const [currentConceptualBudgetId, setCurrentConceptualBudgetId] = useState<string | null>(null);
  const [showInsights, setShowInsights] = useState(false);
  const [snapshotState, setSnapshotState] = useState<any | null>(null);
  const [userOrgs, setUserOrgs] = useState<Array<{ id: string; name: string }>>([]);
  const [showAuth, setShowAuth] = useState(false);
  const [authInitialMode, setAuthInitialMode] = useState<"login" | "signup" | "new-password">("login");
  const [authInitialEmail, setAuthInitialEmail] = useState("");
  const [isApproved, setIsApproved] = useState<boolean | null>(null); // null = loading
  const [showSecurity, setShowSecurity] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [verifyResendSent, setVerifyResendSent] = useState(false);
  const [verifyResending, setVerifyResending] = useState(false);

  // ── Sync hash → state on mount and popstate (back/forward) ──
  const applyHash = React.useCallback(() => {
    const { route, id } = parseHash();
    // Reset everything first
    setCurrentProjectId(null); setCurrentProjectName(null); setCurrentOrgId(null);
    setSettingsTab(null); setSandboxMode(false); setSnapshotState(null);
    setCurrentConceptualBudgetId(null); setShowInsights(false); setShowSecurity(false);
    setShowPrivacy(false); setShowTerms(false);

    if (route === "signup") { setAuthInitialMode("signup"); setAuthInitialEmail(decodeURIComponent(id || "")); setShowAuth(true); }
    else if (route === "security") setShowSecurity(true);
    else if (route === "privacy") setShowPrivacy(true);
    else if (route === "terms") setShowTerms(true);
    else if (route === "budget" && id) setCurrentConceptualBudgetId(id);
    else if (route === "insights" || route === "benchmarks") setShowInsights(true);
    else if (route === "sandbox") setSandboxMode(true);
    else if (route === "settings" && id) setSettingsTab(id);
    else if (route === "project" && id) { setCurrentProjectId(id); setCurrentProjectName(""); }
    // else: home / project picker
  }, []);

  useEffect(() => {
    // On mount: check query params first (survives Outlook SafeLinks, Mimecast,
    // and other corporate email rewriters that strip URL fragments), then fall
    // back to the hash for routes set within the app.
    const params = new URLSearchParams(window.location.search);
    const signupEmail = params.get("signup");
    // Password-reset links (#access_token=...&type=recovery) are handled entirely
    // by useAuth's onAuthStateChange PASSWORD_RECOVERY handler, which only fires
    // after Supabase has established the session. Don't touch those links here —
    // acting on the hash before the session is ready causes "Auth session missing!".
    const isRecoveryLink = window.location.hash.includes("type=recovery");
    if (signupEmail) {
      setAuthInitialMode("signup");
      setAuthInitialEmail(signupEmail);
      setShowAuth(true);
      // Clean the query param from the URL so a refresh doesn't re-open the form
      window.history.replaceState(null, "", window.location.pathname);
    } else if (!isRecoveryLink) {
      const { route } = parseHash();
      if (route) applyHash();
    }
    const onPop = () => applyHash();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [applyHash]);

  // ── Sync state → hash whenever view changes ──
  useEffect(() => {
    if (showSecurity) setHash("security");
    else if (showPrivacy) setHash("privacy");
    else if (showTerms) setHash("terms");
    else if (currentConceptualBudgetId) setHash(`budget/${currentConceptualBudgetId}`);
    else if (showInsights) setHash("insights");
    else if (sandboxMode) setHash("sandbox");
    else if (settingsTab) setHash(`settings/${settingsTab}`);
    else if (currentProjectId) setHash(`project/${currentProjectId}`);
    else setHash("");
  }, [showSecurity, showPrivacy, showTerms, currentConceptualBudgetId, showInsights, sandboxMode, settingsTab, currentProjectId]);

  // Check is_approved whenever user changes
  useEffect(() => {
    if (!user || !supabase) { setIsApproved(null); return; }
    supabase
      .from("profiles")
      .select("is_approved")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        setIsApproved(data?.is_approved ?? false);
      });
  }, [user]);

  // Load user's organizations
  useEffect(() => {
    if (!user || !supabase) { setUserOrgs([]); return; }
    supabase
      .from("org_members")
      .select("org_id, organizations ( id, name )")
      .eq("user_id", user.id)
      .then(({ data }) => {
        setUserOrgs(
          (data || []).map((m: any) => ({ id: m.organizations?.id || m.org_id, name: m.organizations?.name || "" }))
        );
      });
  }, [user]);

  // Create one SupabaseStorageProvider instance per logged-in user
  const supabaseStorage = useMemo(() => {
    if (!user || !supabase) return null;
    return new SupabaseStorageProvider(user.id);
  }, [user]);

  // When user logs in or out, reset view/auth state appropriately
  useEffect(() => {
    if (!user) {
      setCurrentProjectId(null);
      setCurrentProjectName(null);
      setCurrentOrgId(null);
      setSettingsTab(null);
    } else {
      // User just authenticated — clear invite modal state so it doesn't persist
      // across sign-outs (e.g. if they sign out and back in, the invite email
      // shouldn't re-open the signup form)
      setShowAuth(false);
      setAuthInitialEmail("");
    }
  }, [user]);

  // ── Analytics: identify user on login, reset on logout ──
  useEffect(() => {
    if (user) {
      identifyUser(user.id, { email: user.email, name: user.displayName });
    } else {
      resetIdentity();
    }
  }, [user?.id]);

  // ── Analytics: track page views on route changes ──
  useEffect(() => {
    const path = window.location.hash || "/";
    capturePageview(path);
  }, [currentProjectId, settingsTab, sandboxMode, showSecurity, showPrivacy, showTerms]);

  const handleOpenProject = (id: string, name: string, orgId?: string | null) => {
    setSettingsTab(null);
    setSnapshotState(null);
    setCurrentProjectId(id);
    setCurrentProjectName(name);
    setCurrentOrgId(orgId ?? null);
    track(Events.PROJECT_OPEN, { projectId: id, hasOrg: !!orgId });
  };

  const handleOpenSnapshot = (projectId: string, projectName: string, state: any) => {
    setSettingsTab(null);
    setSnapshotState(state);
    setCurrentProjectId(projectId);
    setCurrentProjectName(projectName);
  };

  const handleOpenSettings = (tab: string) => {
    setCurrentProjectId(null);
    setCurrentProjectName(null);
    setSettingsTab(tab);
  };

  const handleOpenSandbox = () => {
    setCurrentProjectId(null);
    setCurrentProjectName(null);
    setSettingsTab(null);
    setSandboxMode(true);
    track(Events.SANDBOX_OPEN);
  };

  const handleOpenConceptualBudget = (id: string) => {
    setCurrentConceptualBudgetId(id);
  };

  const handleOpenInsights = () => {
    setShowInsights(true);
  };

  const handleBackToProjects = () => {
    setCurrentProjectId(null);
    setCurrentProjectName(null);
    setCurrentOrgId(null);
    setSettingsTab(null);
    setSandboxMode(false);
    setSnapshotState(null);
    setCurrentConceptualBudgetId(null);
    setShowInsights(false);
  };

  const handleSignOut = async () => {
    setCurrentProjectId(null);
    setCurrentProjectName(null);
    setSettingsTab(null);
    setSandboxMode(false);
    track(Events.LOGOUT);
    await signOut();
  };

  // Legal / info pages — accessible without login
  if (showSecurity) return <SecurityPage onBack={handleBackToProjects} />;
  if (showPrivacy)  return <PrivacyPolicyPage onBack={handleBackToProjects} />;
  if (showTerms)    return <TermsPage onBack={handleBackToProjects} />;

  // Loading state
  if (loading) {
    return (
      <div style={{ fontFamily: FONT, background: "#0f1117", color: "#e8e6e1", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}><OELogo height={50} circleColor="white" barColor="#4fd1c5"/></div>
          <div style={{ fontSize: 12, color: "#708898" }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Password recovery — user clicked a reset link; show new-password form fullscreen
  if (isPasswordRecovery) {
    return (
      <AuthPage
        initialMode="new-password"
        onClose={clearPasswordRecovery}
      />
    );
  }

  // Supabase configured + not logged in → Landing page + optional auth modal overlay
  if (supabase && !user) {
    const handleShowAuth = (mode: "login" | "signup") => {
      setAuthInitialMode(mode);
      setAuthInitialEmail("");
      setShowAuth(true);
      // Push a distinct URL so the browser back button dismisses the modal
      if (window.location.hash !== "#/login") window.history.pushState(null, "", "#/login");
    };
    const handleCloseAuth = () => {
      setShowAuth(false);
      setAuthInitialEmail("");
      window.history.pushState(null, "", "#/");
    };
    return (
      <>
        <LandingPage
          onLogin={() => handleShowAuth("login")}
          onGetStarted={() => handleShowAuth("signup")}
        />
        {showAuth && (
          <AuthPage
            initialMode={authInitialMode}
            initialEmail={authInitialEmail}
            onClose={handleCloseAuth}
          />
        )}
      </>
    );
  }

  // Email verification gate — shown when email/password signup hasn't been confirmed yet
  if (supabase && user && !emailVerified) {
    const handleResend = async () => {
      setVerifyResending(true);
      await supabase!.auth.resend({ type: "signup", email: user.email });
      setVerifyResending(false);
      setVerifyResendSent(true);
    };
    return (
      <div style={{ fontFamily: FONT, background: "#0f1117", color: "#e8e6e1", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 460 }}>
          <div style={{ marginBottom: 20, display: "flex", justifyContent: "center" }}><Icon.Envelope size={36}/></div>
          <div style={{marginBottom:14,display:"flex",justifyContent:"center"}}><OELogo height={50} circleColor="white" barColor="#4fd1c5"/></div>
          <h2 style={{ margin: "0 0 14px", fontSize: 20, fontWeight: 700, color: "#e8e6e1" }}>
            Check your inbox
          </h2>
          <p style={{ fontSize: 14, color: "#708898", lineHeight: 1.7, margin: "0 0 8px" }}>
            We sent a confirmation link to
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: "#e8e6e1", margin: "0 0 28px" }}>
            {user.email}
          </p>
          <p style={{ fontSize: 13, color: "#708898", lineHeight: 1.6, margin: "0 0 28px" }}>
            Click the link in that email to activate your account. Check your spam folder if you don't see it within a minute.
          </p>
          {verifyResendSent ? (
            <p style={{ fontSize: 13, color: "#4fd1c5", margin: "0 0 24px" }}>✓ Sent! Check your inbox again.</p>
          ) : (
            <button onClick={handleResend} disabled={verifyResending}
              style={{ background: "#4fd1c5", border: "none", color: "#0f1117", padding: "12px 28px", borderRadius: 6, fontFamily: FONT, fontSize: 14, fontWeight: 700, cursor: verifyResending ? "default" : "pointer", marginBottom: 16, opacity: verifyResending ? 0.6 : 1 }}>
              {verifyResending ? "Sending…" : "Resend confirmation email"}
            </button>
          )}
          <br />
          <button onClick={handleSignOut}
            style={{ background: "none", border: "1px solid #2d3748", color: "#708898", padding: "10px 24px", borderRadius: 6, fontFamily: FONT, fontSize: 13, cursor: "pointer", marginTop: 8 }}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Supabase configured + logged in → check approval
  if (supabase && user && isApproved === null) {
    return (
      <div style={{ fontFamily: FONT, background: "#0f1117", color: "#e8e6e1", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 12, color: "#708898" }}>Loading…</div>
      </div>
    );
  }

  if (supabase && user && isApproved === false) {
    return (
      <div style={{ fontFamily: FONT, background: "#0f1117", color: "#e8e6e1", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 440 }}>
          <div style={{ marginBottom: 20, display: "flex", justifyContent: "center" }}><Icon.Hourglass size={36}/></div>
          <div style={{marginBottom:14,display:"flex",justifyContent:"center"}}><OELogo height={50} circleColor="white" barColor="#4fd1c5"/></div>
          <h2 style={{ margin: "0 0 14px", fontSize: 20, fontWeight: 700, color: "#e8e6e1" }}>
            Access pending approval
          </h2>
          <p style={{ fontSize: 14, color: "#708898", lineHeight: 1.7, margin: "0 0 32px" }}>
            Your account is awaiting approval. We'll send you an email at <strong style={{ color: "#e8e6e1" }}>{user.email}</strong> once access is granted.
          </p>
          <button onClick={handleSignOut}
            style={{ background: "none", border: "1px solid #2d3748", color: "#708898", padding: "10px 24px", borderRadius: 6, fontFamily: FONT, fontSize: 13, cursor: "pointer" }}>
            Sign out
          </button>
        </div>
      </div>
    );
  }


  // Supabase configured + logged in + conceptual budget open
  if (supabase && user && currentConceptualBudgetId) {
    return <ConceptualBudgetEditor budgetId={currentConceptualBudgetId} onBack={handleBackToProjects} storage={supabaseStorage} />;
  }

  // Supabase configured + logged in + insights open (includes Compare Projects tab)
  if (supabase && user && supabaseStorage && showInsights) {
    return <InsightsView storage={supabaseStorage} user={user} onBack={handleBackToProjects} />;
  }

  // Supabase configured + logged in + project open → Cloud estimate mode
  if (supabase && user && supabaseStorage && currentProjectId) {
    return (
      <StorageContext.Provider value={supabaseStorage}>
        <OpenEstimate
          user={user}
          signOut={handleSignOut}
          cloudProjectId={currentProjectId}
          cloudStorage={supabaseStorage}
          onBackToProjects={handleBackToProjects}
          initialState={snapshotState ?? undefined}
          initialLocked={snapshotState != null}
          userOrgs={userOrgs}
          currentOrgId={currentOrgId}
        />
      </StorageContext.Provider>
    );
  }

  // Supabase configured + logged in + settings tab open → Settings-only mode
  if (supabase && user && supabaseStorage && settingsTab) {
    return (
      <StorageContext.Provider value={supabaseStorage}>
        <OpenEstimate
          user={user}
          signOut={handleSignOut}
          cloudStorage={supabaseStorage}
          initialTab={settingsTab}
          onBackToProjects={handleBackToProjects}
          userOrgs={userOrgs}
        />
      </StorageContext.Provider>
    );
  }

  // Supabase configured + logged in + sandbox mode → in-memory demo, nothing saved
  if (supabase && user && supabaseStorage && sandboxMode) {
    return (
      <StorageContext.Provider value={supabaseStorage}>
        <OpenEstimate
          user={user}
          signOut={handleSignOut}
          isSandbox={true}
          initialState={buildSandboxProject()}
          initialTab="estimate"
          onBackToProjects={handleBackToProjects}
          userOrgs={userOrgs}
        />
      </StorageContext.Provider>
    );
  }

  // Supabase configured + logged in + nothing selected → Project Picker
  if (supabase && user && supabaseStorage) {
    const handleOnboardingClose = () => {
      markOnboardingSeen();
      setShowOnboarding(false);
    };
    return (
      <>
        <ProjectPicker
          storage={supabaseStorage}
          user={user}
          userOrgs={userOrgs}
          onOpen={handleOpenProject}
          onOpenSnapshot={handleOpenSnapshot}
          onOpenSettings={handleOpenSettings}
          onSandbox={handleOpenSandbox}
          onSignOut={handleSignOut}
          onOpenConceptualBudget={handleOpenConceptualBudget}
          onOpenInsights={handleOpenInsights}
          onFirstLoad={() => { if (shouldShowOnboarding()) setShowOnboarding(true); }}
        />
        {showOnboarding && (
          <OnboardingModal
            onClose={handleOnboardingClose}
            onOpenSandbox={() => { handleOnboardingClose(); handleOpenSandbox(); }}
          />
        )}
        <CookieBanner />
      </>
    );
  }

  // No Supabase configured → localStorage fallback (offline / dev mode)
  return (
    <>
      <StorageContext.Provider value={localStorageProvider}>
        <OpenEstimate user={user} signOut={signOut} />
      </StorageContext.Provider>
      <CookieBanner />
    </>
  );
}

export default function App() {
  const auth = useAuthProvider();

  return (
    <AuthContext.Provider value={auth}>
      <AppContent />
    </AuthContext.Provider>
  );
}
